import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { updateLineFriendTestUser } from 'api/line';
import { queryKeys } from 'constants/queryKeys';
import { userStrings } from 'constants/strings';
import { useAuth } from 'contexts/AuthContext';
import { toErrorMessage } from 'hooks/utils';

type UseUpdateLineFriendTestUserResult = {
  toggle: (userLineId: number, isTestUser: boolean) => void;
  pendingUserLineIds: number[];
  error: string | undefined;
};

export function useUpdateLineFriendTestUser(): UseUpdateLineFriendTestUserResult {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const [pendingUserLineIds, setPendingUserLineIds] = useState<number[]>([]);
  const [error, setError] = useState<string | undefined>();

  const mutation = useMutation({
    mutationFn: async (params: {
      userLineId: number;
      isTestUser: boolean;
    }) => {
      if (!user) {
        throw new Error(userStrings.edit.errors.unauthorized);
      }

      return updateLineFriendTestUser(params.userLineId, params.isTestUser);
    },
    onSuccess: (_response, variables) => {
      queryClient.invalidateQueries({
        queryKey: [queryKeys.LINE_FRIENDS, user?.uid],
      });
      queryClient.invalidateQueries({
        queryKey: [
          queryKeys.LINE_FRIEND_DETAIL,
          variables.userLineId,
          user?.uid,
        ],
      });
      setError(undefined);
    },
    onError: (caught) => {
      setError(
        toErrorMessage(caught, userStrings.errors.updateTestUserFailed),
      );
    },
    onSettled: (_response, _caught, variables) => {
      setPendingUserLineIds((prev) =>
        prev.filter((id) => id !== variables.userLineId),
      );
    },
  });

  const toggle = (userLineId: number, isTestUser: boolean) => {
    setError(undefined);
    setPendingUserLineIds((prev) =>
      prev.includes(userLineId) ? prev : [...prev, userLineId],
    );
    mutation.mutate({ userLineId, isTestUser });
  };

  return {
    toggle,
    pendingUserLineIds,
    error,
  };
}
